import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import useIncidenciasPanel from '../../hooks/useIncidenciasPanel';
import useConfiguracion from '../../hooks/useConfiguracion';
import useAuth from '../../hooks/useAuth';
import MapaAlertasVivo from '../../components/Panel/MapaAlertasVivo';
import ToastNotificaciones from '../../components/Panel/ToastNotificaciones';
import { reproducirSonidoAlerta } from '../../utils/sonidoAlerta';
import CampaignHero from '../../components/campaign/CampaignHero';
import PanelShell from './PanelShell';
import '../../styles/Panel.css';

const ICONO_POR_ESTADO = {
  pendiente: 'fa-clock',
  en_progreso: 'fa-spinner',
  culminado: 'fa-circle-check',
  anulado: 'fa-ban',
};

// Mismos colores que ya usa `Reportes.jsx` (`COLORES_ESTADO`) — así una
// incidencia "pendiente" se ve del mismo color acá y en los gráficos,
// nunca dos paletas distintas para el mismo estado (HU-010 Fase 1).
const COLOR_POR_ESTADO = {
  pendiente: '#f5a623',
  en_progreso: '#4a90d9',
  culminado: '#2ecc71',
  anulado: '#95a5a6',
};

const REFRESCO_MAPA_MS = 20_000;

// Fecha de "hoy" en la zona horaria configurada (no en UTC): `toISOString()`
// da la fecha UTC, así que por la noche en Lima ya devolvería "mañana" y este
// dashboard —que filtra por día— aparecería en cero sin motivo.
const hoyISO = (zona) => {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: zona || 'America/Lima',
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
};

/**
 * Pantalla de inicio del panel (HU-004 Fase 1; ampliada HU-010) — todo el
 * dashboard es "por día": tarjetas de estado (Fase 1), ranking Top 10 de
 * categorías con más incidencias hoy (Fase 2, calculado sobre
 * `estadisticas_incidencias` filtrado a la fecha de hoy) y mapa en vivo con
 * las incidencias de hoy (Fase 3, `incidencias_mapa_hoy`, refrescado por
 * polling). Exclusivo `ADMINISTRADOR`/`SUPERADMIN` (gateado en `PanelRoutes`).
 */
const AlertasDelDia = () => {
  const { loading, error, obtenerAlertasDelDia, obtenerEstadisticas, obtenerMapaHoy } = useIncidenciasPanel();
  const { configuracion } = useConfiguracion();
  const { user } = useAuth();

  const [alertas, setAlertas] = useState(null);
  const [categoriasHoy, setCategoriasHoy] = useState(null);
  const [totalHoyParaRanking, setTotalHoyParaRanking] = useState(0);
  const [barrasListas, setBarrasListas] = useState(false);
  const [incidenciasMapa, setIncidenciasMapa] = useState([]);
  const [mapaExpandido, setMapaExpandido] = useState(false);
  const [notificaciones, setNotificaciones] = useState([]);
  const intervaloRef = useRef(null);
  // `null` hasta la primera carga — así la primera tanda de incidencias de
  // hoy nunca dispara notificaciones (serían todas "nuevas" comparadas
  // contra un set vacío); recién a partir de la segunda vuelta de polling
  // se avisa de lo que realmente acaba de entrar.
  const idsConocidosRef = useRef(null);

  const cargar = useCallback(async () => {
    const resultado = await obtenerAlertasDelDia();
    if (resultado.success) setAlertas(resultado.alertas);

    // Ranking Top 10 (Fase 2): mismo endpoint que ya usa Reportes, filtrado
    // a hoy en ambos extremos del rango — el dashboard entero es "por día".
    const hoy = hoyISO(configuracion.zona_horaria);
    const resultadoStats = await obtenerEstadisticas({ fecha_desde: hoy, fecha_hasta: hoy });
    if (resultadoStats.success) {
      setCategoriasHoy(resultadoStats.estadisticas.por_categoria || []);
      setTotalHoyParaRanking(resultadoStats.estadisticas.total_incidencias || 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // Dispara la animación de "carga" de las barras recién cuando ya hay datos
  // — si el ancho inicial ya saliera en su valor final no habría transición
  // que animar (el truco es arrancar en 0% y mover a la meta un tick después).
  useEffect(() => {
    if (categoriasHoy) {
      const id = setTimeout(() => setBarrasListas(true), 80);
      return () => clearTimeout(id);
    }
  }, [categoriasHoy]);

  // Notificación de incidencias nuevas (HU-012) — widget arriba a la derecha
  // con sonido, se autodestruye sola a los 6s. No hay WebSockets en este
  // proyecto; se reusa el mismo polling de 20s del mapa en vivo para
  // detectar qué `inc_inId` no estaba en la tanda anterior.
  const notificarNuevas = (incidenciasDeHoy) => {
    if (!idsConocidosRef.current) {
      idsConocidosRef.current = new Set(incidenciasDeHoy.map((i) => i.inc_inId));
      return;
    }
    const nuevas = incidenciasDeHoy.filter((i) => !idsConocidosRef.current.has(i.inc_inId));
    idsConocidosRef.current = new Set(incidenciasDeHoy.map((i) => i.inc_inId));
    if (nuevas.length === 0) return;

    reproducirSonidoAlerta();
    const notifs = nuevas.map((i) => ({
      id: `${i.inc_inId}-${Date.now()}`,
      texto: `${i.categoria || 'Sin categoría'} — ${i.estado}`,
    }));
    setNotificaciones((actual) => [...actual, ...notifs]);
    notifs.forEach((n) => {
      setTimeout(() => {
        setNotificaciones((actual) => actual.filter((x) => x.id !== n.id));
      }, 6000);
    });
  };

  // Mapa en vivo (Fase 3): primera carga inmediata + polling cada 20s
  // mientras la pantalla esté montada — así las boyas nuevas "van
  // apareciendo" a medida que entran incidencias, sin recargar la página.
  useEffect(() => {
    const cargarMapa = async () => {
      const resultado = await obtenerMapaHoy();
      if (resultado.success) {
        notificarNuevas(resultado.incidencias);
        setIncidenciasMapa(resultado.incidencias);
      }
    };
    cargarMapa();
    intervaloRef.current = setInterval(cargarMapa, REFRESCO_MAPA_MS);
    return () => clearInterval(intervaloRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const top10 = useMemo(() => (categoriasHoy || []).slice(0, 10), [categoriasHoy]);

  return (
    <PanelShell>
      <ToastNotificaciones
        notificaciones={notificaciones}
        onDescartar={(id) => setNotificaciones((actual) => actual.filter((n) => n.id !== id))}
      />
      <h2>Dashboard</h2>
      <p className="panel-texto-ayuda">
        {alertas ? `Incidencias registradas hoy, ${alertas.fecha}` : 'Cargando…'}
      </p>

      {error && <p className="panel-gu-mensaje">{error}</p>}

      {/* Se monta recién cuando `user` ya resolvió — así el estado
          plegado/abierto (leído de localStorage por usuario dentro de
          CampaignHero, en su primer render) usa la clave correcta desde el
          principio, en vez de arrancar con "anónimo" y pisar después el
          valor real guardado del usuario. */}
      {user && <CampaignHero configuracion={configuracion} usuarioId={user.usu_inId} />}

      {alertas && (
        <div className="panel-alertas-grid">
          <div className="panel-alerta-tarjeta panel-alerta-tarjeta-total" style={{ '--color-estado': 'var(--brand)' }}>
            <i className="fas fa-layer-group panel-alerta-icono"></i>
            <i className="fas fa-layer-group panel-alerta-icono-fondo"></i>
            <span className="panel-alerta-cantidad">{alertas.total_hoy}</span>
            <span className="panel-alerta-etiqueta">Total de hoy</span>
          </div>
          {alertas.por_estado.map((item) => (
            <div
              key={item.estado}
              className="panel-alerta-tarjeta"
              style={{ '--color-estado': COLOR_POR_ESTADO[item.estado] || 'var(--brand)' }}
            >
              <i className={`fas ${ICONO_POR_ESTADO[item.estado] || 'fa-circle'} panel-alerta-icono`}></i>
              <i className={`fas ${ICONO_POR_ESTADO[item.estado] || 'fa-circle'} panel-alerta-icono-fondo`}></i>
              <span className="panel-alerta-cantidad">{item.cantidad}</span>
              <span className="panel-alerta-etiqueta">{item.estado_display}</span>
            </div>
          ))}
        </div>
      )}

      {loading && !alertas && <p className="panel-texto-ayuda">Cargando alertas…</p>}

      <div className="panel-dashboard-fila-2col">
        <div className="panel-config-tarjeta">
          <span className="panel-config-tarjeta-titulo"><i className="fas fa-ranking-star"></i> Top 10 categorías (hoy)</span>

          {!categoriasHoy && <p className="panel-texto-ayuda">Cargando…</p>}
          {categoriasHoy && top10.length === 0 && <p className="panel-texto-ayuda">Sin incidencias hoy todavía.</p>}

          <div className="panel-ranking-lista">
            {top10.map((item, indice) => {
              const porcentaje = totalHoyParaRanking > 0 ? (item.cantidad / totalHoyParaRanking) * 100 : 0;
              return (
                <div key={item.categoria} className="panel-ranking-fila">
                  <span className="panel-ranking-puesto">{indice + 1}</span>
                  <div className="panel-ranking-info">
                    <div className="panel-ranking-cabecera">
                      <span className="panel-ranking-nombre">{item.categoria}</span>
                      <span className="panel-ranking-cantidad">{item.cantidad} · {porcentaje.toFixed(1)}%</span>
                    </div>
                    <div className="panel-ranking-barra-fondo">
                      <div
                        className="panel-ranking-barra-relleno"
                        style={{ width: barrasListas ? `${porcentaje}%` : '0%' }}
                      ></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <MapaAlertasVivo
          centro={{ lat: Number(configuracion.mapa_lat), lng: Number(configuracion.mapa_lng) }}
          incidencias={incidenciasMapa}
          expandido={mapaExpandido}
          onToggleExpandido={() => setMapaExpandido((v) => !v)}
        />
      </div>
    </PanelShell>
  );
};

export default AlertasDelDia;
